import React, { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Check from '@material-ui/icons/Check';
import { setStep, openModal, setAccessoriesCategory, setPrintActions } from 'src/redux/configurator/actions';
import { clearPreviewedPack } from 'src/redux/bike/actions';
import { currentStepSelector, completedStepsSelector } from 'src/redux/configurator/selectors';
import Label from 'components/_utility/Label';
import { getViewportWidth } from 'helpers/utilities';
import smoothscroll from 'smoothscroll-polyfill';
import useDataLayer from 'src/hooks/useDataLayer';
import useSteps from 'src/hooks/useSteps';
import { Scroller, Wrap, Step, StepLink, RightShade, LeftShade, CheckIcon, StepNumber } from './Stepper.styled';

const Stepper = ({ isVisible }) => {
  const [showScrollLeft, toggleShowScrollLeft] = React.useState(false);
  const [showScrollRight, toggleShowScrollRight] = React.useState(true);
  const dispatch = useDispatch();
  const steps = useSteps();
  const activeStep = useSelector(currentStepSelector);
  const scrollerRef = useRef();
  const completedSteps = useSelector(completedStepsSelector);
  const pushToDataLayer = useDataLayer();

  // Show and hide scroll hint gradients
  useEffect(() => {
    const element = scrollerRef.current;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth } = element;

      toggleShowScrollLeft(scrollLeft > 0);
      toggleShowScrollRight(scrollLeft < scrollWidth - getViewportWidth());
    };

    // eslint-disable-next-line no-unused-expressions
    element?.addEventListener?.('scroll', handleScroll);

    return () => {
      // eslint-disable-next-line no-unused-expressions
      element?.removeEventListener?.('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    smoothscroll.polyfill();
  }, []);

  // Bring active step button into view when changed
  useEffect(() => {
    const activeStepIndex = steps.indexOf(steps.find(step => step.number === activeStep));
    const activeStepEl = document.querySelector(`.js-step-${activeStepIndex}`);

    const centralPoint = getViewportWidth() / 2 - activeStepEl.offsetWidth / 2;
    const scrollLeft = activeStepEl.offsetLeft;

    if (typeof scrollerRef.current.scrollTo === 'function') {
      scrollerRef.current.scrollTo({
        top: 0,
        left: scrollLeft < centralPoint ? 0 : centralPoint,
        behaviour: 'smooth',
      });
    }
  }, [activeStep, steps]);

  return (
    <Wrap isVisible={isVisible}>
      <Scroller ref={scrollerRef}>
        {steps.map((step, index) => {
          const completed = completedSteps.includes(step.number);

          const label = (
            <>
              <StepNumber check={completed}>{step.number}.</StepNumber>
              {completed ? (
                <CheckIcon>
                  <Check />
                </CheckIcon>
              ) : null}
              <Label name={step.label} />
            </>
          );

          return step.component ? (
            <Step
              ref={step.ref}
              type="button"
              key={step.number}
              onClick={() => [
                dispatch(setStep(step.number)),
                dispatch(clearPreviewedPack()),
                dispatch(setPrintActions(0)),
                step.accessories && dispatch(setAccessoriesCategory(null)),
                step.gtmEvent && pushToDataLayer(step.gtmEvent),
              ]}
              active={steps[index].active}
              bold={completed}
              className={`js-step-${index}`}
            >
              {label}
            </Step>
          ) : (
            <StepLink
              ref={step.ref}
              key={step.number}
              to={step.link}
              bold={step.bold}
              onClick={e => {
                e.preventDefault();
                dispatch(openModal('unsavedConfigurationAbandon'));
              }}
            >
              {label}
            </StepLink>
          );
        })}
      </Scroller>
      <LeftShade visible={showScrollLeft} />
      <RightShade visible={showScrollRight} />
    </Wrap>
  );
};

export default Stepper;
